<template>
  <f7-page name="profilePage">
    <div class="profile-content">
      <div class="sub-menu-wrapper">
        <SubMenu :items="MenuOptions.contexts.clientArea.items" />
      </div>
      <div v-if="!loaded" class="preloader-overlay">
        <div class="preloader-card">
          <f7-preloader></f7-preloader>
          <div class="preloader-text">
            {{ $t('loading') }}
          </div>
        </div>
      </div>
      <Profile
        ref="profileCmp"
        @languageChanged="newLanguageSelected"
        @avatar-changed="avatarchanged"
        @save-personal-data="saveNewPersonalData"
      />
    </div>
  </f7-page>
</template>

<script>
import Profile from '@/components/Profile/index.vue';
import SubMenu from '@/components/SubMenu/index.vue';
import MenuOptions from '@/json/menuOptions.json';
import { mapActions, mapState, mapGetters } from 'vuex';
import esMessages from '@/json/langs/es.json';
import frMessages from '@/json/langs/fr.json';
import ptMessages from '@/json/langs/pt.json';
import enMessages from '@/json/langs/en.json';
import itMessages from '@/json/langs/it.json';
import { locale, loadMessages } from 'devextreme/localization';
import L from 'leaflet';
import { ALL_LANGUAGES, NOTIFY_TIME_LONG } from '@/js/constants';

export default {
  components: {
    Profile,
    SubMenu,
  },
  data() {
    return {
      lastLanguageSelected: '',
      avatarSelected: '',
      languageKey: '',
      loaded: false,
      MenuOptions,
      langsDx: {
        esMessages,
        frMessages,
        ptMessages,
        enMessages,
        itMessages,
      },
    };
  },
  computed: {
    ...mapGetters('user', ['isAdmin']),
    ...mapGetters('user', ['userProfile']),
    ...mapState('user', ['userPasswords']),
  },
  mounted() {
    this.setCurrentRoute([
      {
        name: this.$t('profile'),
        disabled: false,
        page: 'ProfilePage',
        active: true,
      },
    ]);
    this.loaded = true;
  },
  methods: {
    newLanguageSelected(lang) {
      this.languageKey = lang.key;
      this.lastLanguageSelected = lang.value;
    },
    avatarchanged(avatar) {
      this.avatarSelected = avatar;
    },
    async saveNewPersonalData() {
      // Capturar ambos valores al iniciar el guardado evita que un evento
      // posterior del DxForm sobrescriba el idioma enviado a la API.
      const requestedLanguage =
        this.lastLanguageSelected ||
        this.$root?.$i18n?.i18next?.language ||
        '';
      const requestedLanguageKey =
        this.languageKey ||
        ALL_LANGUAGES.find((lang) => lang.value === requestedLanguage)
          ?.key ||
        '';
      this.loaded = false;
      const { name, surname, phone } = this.userProfile;

      try {
        this.$notifyDX(
          {
            message: this.$t('updating_personal_information'),
            width: 450,
          },
          'info',
          2000
        );

        if (this.avatarSelected) {
          await this.updateAvatar({ newAvatar: this.avatarSelected });
        }

        const updatePayload = {
          name,
          surname,
          phone,
          language: requestedLanguageKey,
        };

        await this.updateUserPersonalData(updatePayload);

        if (requestedLanguage) {
          // Sincronizar la interfaz con el mismo idioma que se ha enviado y
          // guardado para el contacto.
          this.updateLanguageInterface(requestedLanguage);
          this.$i18n.i18next.changeLanguage(requestedLanguage);

          loadMessages(this.langsDx[`${requestedLanguage}Messages`]);
          locale(
            `${requestedLanguage}-${requestedLanguage.toUpperCase()}`
          );
          L.PM.activeLang = requestedLanguage;
        }

        const refreshedUserData = await this.getUserData();
        const { contacts } = refreshedUserData;

        this.updateUserContacts(contacts);

        this.$notifyDX(
          {
            message: this.$t('personal_information_updated'),
            width: 450,
          },
          'success',
          2000
        );
      } catch (error) {
        this.$f7.dialog.close();

        this.$notifyDX(
          {
            message: this.$t(error.errorCodes?.[0] ?? ''),
            width: 450,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.$f7.dialog.close();

        // Si hay intención de cambiar password (cualquier campo relleno), delega en Profile (con validación)
        const p = this.userPasswords || {};
        const hasAnyPasswordField =
          (p.oldPassword || '').trim() ||
          (p.newPassword || '').trim() ||
          (p.confirmNewPassword || '').trim();

        if (hasAnyPasswordField) {
          const { profileCmp } = this.$refs || {};

          if (
            !profileCmp ||
            typeof profileCmp.saveNewPassword !== 'function'
          ) {
            this.$notifyDX(
              {
                message: this.$t('defaultError'),
                width: 450,
              },
              'error',
              NOTIFY_TIME_LONG
            );
          } else {
            try {
              await profileCmp.saveNewPassword();
            } catch {
              this.$notifyDX(
                {
                  message: this.$t('defaultError'),
                  width: 450,
                },
                'error',
                NOTIFY_TIME_LONG
              );
            }
          }
        }

        this.loaded = true;
      }
    },
    goToBack() {
      this.$f7router.navigate(
        { name: this.$f7router.previousRoute.name },
        {
          animate: true,
          transition: 'f7-fade',
        }
      );
    },
    ...mapActions('breadcrumb', ['setCurrentRoute']),
    ...mapActions('user', [
      'updateLanguageInterface',
      'updateUserPersonalData',
      'updatePassword',
      'setUserNewPassword',
      'setUserOldPassword',
      'setConfirmNewPassword',
      'updateAvatar',
      'getUserData',
      'updateUserContacts',
    ]),
  },
};
</script>
